import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { useMemo, VFC } from 'react';
import styled from 'styled-components';

import { valueTransformer } from 'dashboard/lib/workflows';
import { WorkflowRunStepError, WorkflowRunStepErrorType } from 'dashboard/models/workflow-version';
import { Paragraph } from 'design-system/components/Text';

const ErrorContainer = styled.div`
  background-color: ${Color.CoreRed100};
  padding: ${Spacing[4]};
  display: flex;
  align-items: center;
  border-radius: 4px;

  & + & {
    margin-top: ${Spacing[4]};
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ErrorContent = styled.div`
  margin-left: ${Spacing[4]};
  word-break: break-word;
`;

const ErrorTitle = styled.div`
  ${Typography.TextLargeBold};
`;

interface WorkflowStepErrorProps {
  error: WorkflowRunStepError;
  tokensToDisplayValue: Record<string, string>;
}

const WorkflowStepError: VFC<WorkflowStepErrorProps> = ({ error, tokensToDisplayValue }) => {
  const { getDisplayValue } = useMemo(
    () => valueTransformer(tokensToDisplayValue),
    [tokensToDisplayValue],
  );

  const errorTitle = useMemo(() => {
    const errorTypeToTitle: Partial<Record<WorkflowRunStepErrorType, string>> = {
      [WorkflowRunStepErrorType.InvalidStepConfigError]: 'Invalid step configuration',
      [WorkflowRunStepErrorType.UnexpectedParameterError]: 'Unexpected parameter',
      [WorkflowRunStepErrorType.InvalidInputError]: 'Invalid input',
      [WorkflowRunStepErrorType.MissingInputError]: 'Missing input',
      [WorkflowRunStepErrorType.UnresolvedPathError]: 'Unresolved path',
      [WorkflowRunStepErrorType.ConfigurationError]: 'Configuration error',
    };

    return errorTypeToTitle[error.type] ?? 'This step encountered an unexpected error';
  }, [error]);

  const errorMessage = useMemo(() => {
    switch (error.type) {
      case WorkflowRunStepErrorType.InvalidStepConfigError:
      case WorkflowRunStepErrorType.ConfigurationError:
      case WorkflowRunStepErrorType.UnexpectedParameterError:
      case WorkflowRunStepErrorType.InvalidInputError:
      case WorkflowRunStepErrorType.MissingInputError:
        return error.message;
      case WorkflowRunStepErrorType.UnresolvedPathError:
        return error.attributes.path
          ? `Unable to resolve path: ${getDisplayValue(error.attributes['path'])}`
          : error.message;
      default:
        return 'Please contact Persona for assistance and resolution.';
    }
  }, [error]);

  return (
    <>
      {error['continued-after-error'] && (
        <ErrorContainer>
          <IconContainer>
            <CircleInfoIcon size={24} />
          </IconContainer>
          <ErrorContent>
            <ErrorTitle>Continued after error</ErrorTitle>
            <Paragraph color='primary'>
              An error occurred when running this step but execution continued because this step was
              marked as Continue on error
            </Paragraph>
          </ErrorContent>
        </ErrorContainer>
      )}
      <ErrorContainer>
        <IconContainer>
          <CircleInfoIcon size={24} />
        </IconContainer>
        <ErrorContent>
          <ErrorTitle>{errorTitle}</ErrorTitle>
          <Paragraph color='primary'>{errorMessage}</Paragraph>
        </ErrorContent>
      </ErrorContainer>
    </>
  );
};

export default WorkflowStepError;
